import { pluralize } from "@common/helpers/string";
import { FiltersTypes } from "@common/types/filters/general";
import { Tag } from "@design-system/Tag/Tag";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { ActiveFilters } from "@components/pageHeader/filters/ActiveFilters/ActiveFilters";
import {
    ActiveFilterButtonLabel,
    getActiveFilterLabel,
} from "@components/pageHeader/filters/ActiveFilterButtonLabel";
import {
    ActiveFilter,
    FiltersChangeFn,
} from "@components/pageHeader/filters/lib";

interface Props {
    /** Creator-set active filters, from `getLockedTaskFilterState`. */
    lockedActiveFilters: ActiveFilter[];
    /** The creator-set task filters the pills read their values from. */
    baseFilters: FiltersTypes;
    maxVisibleFilters?: number;
    /** Implicit filters applied on top of the locked ones; shown as plain tags. */
    implicitFilterLabels?: string[];
    /**
     * Folds the pills into one "N filters" button; hovering it lists them.
     * A single filter still renders as its own pill.
     */
    collapse?: boolean;
}

const NOOP_FILTERS_CHANGE: FiltersChangeFn = () => {};
const NOOP_CLEAR_FILTERS = () => {};

/**
 * Renders the filters baked into a transaction task by its creator as
 * disabled, non-removable pills.
 */
export const LockedTaskFilters = ({
    lockedActiveFilters,
    baseFilters,
    maxVisibleFilters = 2,
    implicitFilterLabels = [],
    collapse = false,
}: Props) => {
    const totalFilters =
        lockedActiveFilters.length + implicitFilterLabels.length;

    if (totalFilters === 0) {
        return null;
    }

    if (collapse && totalFilters > 1) {
        return (
            <Tooltip
                bullets={[
                    ...lockedActiveFilters.map((filterId) =>
                        getActiveFilterLabel({
                            filterId,
                            filters: baseFilters,
                        }),
                    ),
                    ...implicitFilterLabels,
                ]}
            >
                <span>
                    <Tag size="lg" color="accent">
                        {`${totalFilters} ${pluralize("filter", totalFilters)}`}
                    </Tag>
                </span>
            </Tooltip>
        );
    }

    return (
        <>
            <ActiveFilters
                activeFilters={lockedActiveFilters}
                renderVisibleFilters={(visibleFilters) =>
                    visibleFilters.map((filterId) => (
                        <ActiveFilterButtonLabel
                            key={filterId}
                            filterId={filterId}
                            filters={baseFilters}
                            onChange={NOOP_FILTERS_CHANGE}
                            disabled
                        />
                    ))
                }
                clearFilters={NOOP_CLEAR_FILTERS}
                hideClearButton
                maxVisibleFilters={maxVisibleFilters}
                disabled
            />
            {implicitFilterLabels.map((label) => (
                <Tag key={label} size="lg" color="accent">
                    {label}
                </Tag>
            ))}
        </>
    );
};
